import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Container, IconButton, LinearProgress, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import currency from "currency.js";
import bkstApi from "../../api";
import BDateTimePicker from "../../common/input/BDateTimePicker";
import {
  getQueryParam,
  getStoreFilterStreets,
  getStoreLocationIdForFilter,
} from "../../common/util";
import ItemFilter from "../../planner/component/ItemFilter";
import { UserContext } from "../../context/UserContext";

const RECEIPT_BG = "#FFFEF7";
const RECEIPT_INK = "#1A1A1A";
const RECEIPT_MUTED = "#5C5C5C";
const RECEIPT_FONT = '"Courier New", Courier, ui-monospace, monospace';
const MIN_REPORT_DATE = "2026-06-12";
const MAX_RANGE_DAYS = 31;

function money(value) {
  const c = currency(value || 0);
  if (c.value < 0) {
    return `(${currency(Math.abs(c.value)).format()})`;
  }
  return c.format();
}

function getDefaultRange() {
  const now = moment();
  const to = now.clone().second(0).millisecond(0);
  const from = now.clone().hour(5).minute(0).second(0).millisecond(0);
  if (to.isBefore(from)) {
    from.subtract(1, "day");
  }
  return {
    from: from.format("YYYY-MM-DDTHH:mm:ss"),
    to: to.format("YYYY-MM-DDTHH:mm:ss"),
  };
}

function dashedRule() {
  return (
    <Box
      aria-hidden
      sx={{
        my: 0.75,
        borderBottom: "1px dashed #BDBDBD",
      }}
    />
  );
}

function ReceiptLine({ label, amount, count, nested, heading, total }) {
  const ink = nested && !heading && !total ? RECEIPT_MUTED : RECEIPT_INK;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 2,
        py: heading || total ? 0.4 : 0.15,
        pl: nested ? 2 : 0,
        fontFamily: RECEIPT_FONT,
        color: ink,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: "inherit",
          fontSize: heading || total ? "0.95rem" : "0.82rem",
          fontWeight: heading || total ? 700 : 400,
          letterSpacing: heading || total ? "0.02em" : 0,
          color: "inherit",
          minWidth: 0,
        }}
      >
        <Box component="span" sx={{ textTransform: heading || total ? "uppercase" : "none" }}>
          {label}
        </Box>
        {count ? ` (x${count})` : ""}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontFamily: "inherit",
          fontSize: heading || total ? "0.95rem" : "0.82rem",
          fontWeight: total ? 700 : heading ? 600 : 400,
          color: "inherit",
          whiteSpace: "nowrap",
          pl: 1,
        }}
      >
        {money(amount)}
      </Typography>
    </Box>
  );
}

function SalesLines({ sales, showCollected, showTipBreakdown }) {
  const summary = sales || { totalSales: 0, tips: 0, taxes: 0, collected: 0 };
  return (
    <Box sx={{ mb: 0.5 }}>
      <ReceiptLine label="Total Sales" amount={summary.totalSales} />
      <ReceiptLine label="Tips" amount={summary.tips} />
      {showTipBreakdown ? (
        <>
          <ReceiptLine label="ToGo" amount={summary.tipsTogo} nested />
          <ReceiptLine label="Future" amount={summary.tipsFuture} nested />
        </>
      ) : null}
      <ReceiptLine label="Taxes" amount={summary.taxes} />
      {showCollected ? (
        <ReceiptLine label="Amount Collected" amount={summary.collected} total />
      ) : null}
    </Box>
  );
}

function StationBlock({ device, overview }) {
  const tenders = device.tenders || [];
  const cash = device.cash;
  const total = device.tenderTotal || { count: 0, amount: 0 };
  const sales = device.sales;

  return (
    <Box>
      {overview ? null : (
        <Typography
          sx={{
            fontFamily: RECEIPT_FONT,
            fontWeight: 700,
            fontSize: "0.95rem",
            textAlign: "center",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            py: 0.5,
          }}
        >
          {device.label}
        </Typography>
      )}
      {overview ? (
        <SalesLines sales={sales} showCollected />
      ) : (
        <>
          <SalesLines sales={sales} showTipBreakdown />
          {tenders.length === 0 ? (
            <Typography
              sx={{
                fontFamily: RECEIPT_FONT,
                fontSize: "0.82rem",
                textAlign: "center",
                color: RECEIPT_MUTED,
                py: 1,
              }}
            >
              No payments
            </Typography>
          ) : (
            <>
              {tenders.map((tender) => (
                <Box key={tender.key} sx={{ mt: 0.75 }}>
                  <ReceiptLine
                    heading
                    label={tender.label}
                    amount={tender.amount}
                    count={tender.count || null}
                  />
                  {tender.key === "card" &&
                    (tender.brands || []).map((brand) => (
                      <ReceiptLine
                        key={`${tender.key}-${brand.key}`}
                        label={brand.label}
                        amount={brand.amount}
                        count={brand.count}
                        nested
                      />
                    ))}
                  {tender.key === "cash" && cash ? (
                    <>
                      <ReceiptLine label="Opening" amount={cash.opening} nested />
                      {(cash.byType || []).map((row) => (
                        <ReceiptLine
                          key={row.key}
                          label={row.label}
                          amount={row.amount}
                          count={row.count}
                          nested
                        />
                      ))}
                      <ReceiptLine label="Expected balance" amount={cash.expectedBalance} nested />
                    </>
                  ) : null}
                </Box>
              ))}
              {dashedRule()}
              <ReceiptLine label="Total" amount={total.amount} count={total.count || null} total />
            </>
          )}
        </>
      )}
    </Box>
  );
}

function Receipt({ devices, totals, rangeLabel }) {
  const stations = devices || [];

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: RECEIPT_BG,
        color: RECEIPT_INK,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        border: "1px solid #E8E4D9",
        px: { xs: 2, sm: 3 },
        pt: 2.5,
        pb: 3,
      }}
    >
      <Typography
        sx={{
          fontFamily: RECEIPT_FONT,
          fontWeight: 700,
          fontSize: "1.15rem",
          textAlign: "center",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        Sales Summary
      </Typography>
      {rangeLabel ? (
        <Typography
          sx={{
            fontFamily: RECEIPT_FONT,
            fontSize: "0.72rem",
            textAlign: "center",
            color: RECEIPT_MUTED,
            mt: 0.5,
            lineHeight: 1.4,
          }}
        >
          {rangeLabel}
        </Typography>
      ) : null}

      {dashedRule()}
      {totals ? (
        <StationBlock device={totals} overview />
      ) : (
        <Typography
          sx={{
            fontFamily: RECEIPT_FONT,
            fontSize: "0.82rem",
            textAlign: "center",
            color: RECEIPT_MUTED,
            py: 1.5,
          }}
        >
          No payments or cash drawer activity in this window
        </Typography>
      )}

      {stations.map((device) => (
        <Box key={device.id}>
          {dashedRule()}
          <StationBlock device={device} />
        </Box>
      ))}
    </Box>
  );
}

export default function SalesSummaryReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState("");
  const [storeFilter, setStoreFilter] = useState({});
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const maxDateTime = moment().toDate();
  const minDateTime = moment(MIN_REPORT_DATE).startOf("day").toDate();
  const defaultReferenceDate = moment().hour(5).minute(0).second(0).millisecond(0).toDate();
  const toReferenceDate = fromDt ? moment(fromDt).toDate() : defaultReferenceDate;

  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      const defaults = getDefaultRange();
      qFrom = defaults.from;
      qTo = defaults.to;
    }
    search(qFrom, qTo, getStoreLocationIdForFilter(user, storeFilter?.store?.value));
  }, [location.search]);

  useEffect(() => {
    if (fromDt && toDt && moment(toDt).isBefore(moment(fromDt))) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > MAX_RANGE_DAYS) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const runSearch = (e) => {
    e.preventDefault();
    if (!fromDt || !toDt || moment(toDt).isBefore(moment(fromDt))) return;
    if (moment(toDt).diff(moment(fromDt), "days") > MAX_RANGE_DAYS) return;
    navigate(
      `/report/sales-summary?from=${encodeURIComponent(fromDt)}&to=${encodeURIComponent(toDt)}`
    );
  };

  const search = (fromDate, toDate, storeLocationId = "") => {
    setLoading(true);
    setError("");
    setPrintError("");
    const locQ = storeLocationId ? `&storeLocationId=${encodeURIComponent(storeLocationId)}` : "";
    bkstApi(
      `/my/report/sales-summary?fromDt=${encodeURIComponent(
        moment(fromDate).toISOString()
      )}&toDt=${encodeURIComponent(moment(toDate).toISOString())}${locQ}`
    )
      .then((res) => {
        setReport(res.data.report);
        setFromDt(fromDate);
        setToDt(toDate);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load sales summary");
        setReport(null);
        setLoading(false);
      });
  };

  const onStoreFilterChange = (o) => {
    const next = { ...storeFilter, ...o };
    const prevId = getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    const nextId = getStoreLocationIdForFilter(user, next?.store?.value);
    setStoreFilter(next);
    if (fromDt && toDt && prevId !== nextId) {
      search(fromDt, toDt, nextId);
    }
  };

  const printerId = user?.config?.receiptPrinterId;
  const printReport = () => {
    if (!printerId || !fromDt || !toDt || printing) return;
    setPrinting(true);
    setPrintError("");
    const storeLocationId = getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    const locQ = storeLocationId ? `&storeLocationId=${encodeURIComponent(storeLocationId)}` : "";
    bkstApi
      .post(
        `/my/printer/${encodeURIComponent(printerId)}/sales-summary?fromDt=${encodeURIComponent(
          moment(fromDt).toISOString()
        )}&toDt=${encodeURIComponent(moment(toDt).toISOString())}${locQ}`
      )
      .then(() => {
        setPrinting(false);
      })
      .catch((e) => {
        setPrintError(e.response?.data?.error || e.message || "Failed to print sales summary");
        setPrinting(false);
      });
  };

  const devices = report?.devices || [];
  const totals = report?.totals;
  const rangeLabel =
    fromDt && toDt
      ? `${moment(fromDt).format("MMM D, YYYY h:mm A")} – ${moment(toDt).format("h:mm A")}`
      : "";

  return (
    <Container maxWidth="sm">
      <Box my={4}>
        <Typography variant="h1" gutterBottom>
          Sales Summary
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Orders placed in this window, plus payments collected at each register.
        </Typography>
        <Box my={3}>
          <form onSubmit={runSearch}>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <BDateTimePicker
                value={fromDt ? moment(fromDt).toDate() : null}
                variant="filled"
                label="From Date"
                onChange={(d) => setFromDt(d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "")}
                required
                hideIcon
                referenceDate={defaultReferenceDate}
                minDateTime={minDateTime}
                maxDateTime={maxDateTime}
              />
              <BDateTimePicker
                value={toDt ? moment(toDt).toDate() : null}
                variant="filled"
                label="To Date"
                onChange={(d) => setToDt(d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "")}
                required
                hideIcon
                referenceDate={toReferenceDate}
                minDateTime={fromDt ? moment(fromDt).toDate() : minDateTime}
                maxDateTime={maxDateTime}
              />
              <Button variant="contained" color="primary" type="submit" style={{ height: "56px" }}>
                <SearchIcon />
              </Button>
            </Box>
          </form>
        </Box>
      </Box>

      {showStoreFilter && (
        <Box my={2}>
          <ItemFilter
            config={[{ name: "store", options: storeStreets }]}
            onChange={onStoreFilterChange}
          />
        </Box>
      )}

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {!loading && !error && report && (
        <>
          {printerId ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <IconButton onClick={printReport} disabled={printing} aria-label="Print">
                <PrintOutlinedIcon />
              </IconButton>
            </Box>
          ) : null}
          {printError ? (
            <Typography color="error" sx={{ mb: 1 }}>
              {printError}
            </Typography>
          ) : null}
          <Receipt devices={devices} totals={totals} rangeLabel={rangeLabel} />
        </>
      )}
    </Container>
  );
}
